import { AppTilePreviewCommand } from 'appservices-utils/lib/data/AppTilePreviewCommand';
import { isEdu } from 'owa-bpos-utils';
import { getCdnUrl } from 'owa-config/lib/bootstrapOptions';
import { OfficeApp } from 'owa-left-rail-utils/lib/store/schema/OfficeApp';
import isMOSLaunchPagesEnabled from 'owa-left-rail-utils/lib/utils/isMOSLaunchPagesEnabled';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { getPaletteAsRawColors } from 'owa-theme';

import type {
    M365ApplicationEdge,
    M365HubApplication,
    M365PlatformApplication,
} from 'owa-graph-schema';
import type NavBarLinkData from 'owa-service/lib/contract/NavBarLinkData';

const CDN_URL = getCdnUrl();

interface M365HubApplicationEdge extends M365ApplicationEdge {
    node: M365HubApplication | M365PlatformApplication;
}

export default function (appId: OfficeApp, navBarLinkData: NavBarLinkData): M365HubApplicationEdge {
    const isEnterprise = !isConsumer() && !isEdu();
    const theme = getPaletteAsRawColors();
    const apps: { [appId: string]: M365HubApplicationEdge } = {
        '9b049ed6-fc8b-40c7-815b-bf84e344d270': {
            node: {
                __typename: 'M365HubApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: '9b049ed6-fc8b-40c7-815b-bf84e344d270',
                        type: 'office_orgexplorer',
                    },
                },
                icon: {
                    __typename: 'InternalIcon',
                    name: 'OutlookOrgExplorerIcon',
                },
                id: '36e5ba55-f1dd-40ab-8389-ad22867ebab4',
                isInUse: false,
                isPinnable: false,
                lockedIndex: 7,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'InternalIcon',
                    name: 'OutlookOrgExplorerIconSelected',
                },
                secondaryCommands: isMOSLaunchPagesEnabled() ? [AppTilePreviewCommand] : [],
            },
        },
        Word: {
            node: {
                __typename: 'M365PlatformApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: 'Word',
                        type: 'office_word',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/word_20x1.svg',
                },
                id: '34318026-c018-414b-abb3-3e32dfb9cc4c',
                isInUse: false,
                isPinnable: false,
                lockedIndex: isEnterprise ? null : 9,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/word_20x1.svg',
                },
                secondaryCommands: [],
            },
        },
        Excel: {
            node: {
                __typename: 'M365PlatformApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: 'Excel',
                        type: 'office_excel',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/excel_20x1.svg',
                },
                id: 'c5251a9b-a95d-4595-91ee-a39e6eed3db2',
                isInUse: false,
                isPinnable: false,
                lockedIndex: isEnterprise ? null : 10,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/excel_20x1.svg',
                },
                secondaryCommands: [],
            },
        },
        Powerpoint: {
            node: {
                __typename: 'M365PlatformApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: 'Powerpoint',
                        type: 'office_powerpoint',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/powerpoint_20x1.svg',
                },
                id: '48cb9ead-1c19-4e1f-8ed9-3d60a7e52b18',
                isInUse: false,
                isPinnable: false,
                lockedIndex: isEnterprise ? null : 11,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/powerpoint_20x1.svg',
                },
                secondaryCommands: [],
            },
        },
        OneNote: {
            node: {
                __typename: 'M365PlatformApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: 'OneNote',
                        type: 'office_onenote',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/onenote_20x1.svg',
                },
                id: '6c548c14-5489-42d8-938e-b45a861fefe6',
                isInUse: false,
                isPinnable: false,
                lockedIndex: null,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20210930.001/assets/brand-icons/product/svg/onenote_20x1.svg',
                },
                secondaryCommands: [],
            },
        },
        'db5e5970-212f-477f-a3fc-2227dc7782bf': {
            node: {
                __typename: 'M365HubApplication',
                accessibleName: navBarLinkData.Title,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: 'db5e5970-212f-477f-a3fc-2227dc7782bf',
                        type: 'office_yammer',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/svg/yammer_16x1.svg',
                },
                id: 'db5e5970-212f-477f-a3fc-2227dc7782bf',
                isInUse: false,
                isPinnable: false,
                lockedIndex: 12,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://static2.sharepointonline.com/files/fabric/assets/brand-icons/product/svg/yammer_16x1.svg',
                },
                secondaryCommands: isMOSLaunchPagesEnabled() ? [AppTilePreviewCommand] : [],
            },
        },
        '4c4ec2e8-4a2c-4bce-8d8f-00fc664a4e5b': {
            node: {
                __typename: 'M365HubApplication',
                accessibleName: navBarLinkData.Text,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: '4c4ec2e8-4a2c-4bce-8d8f-00fc664a4e5b',
                        type: 'outlook_bookings',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src:
                        'https://shell.cdn.office.net/shellux/bookings_mono.3a4233706c113a988a2b7c2397b6bfce.svg',
                },
                id: '4c4ec2e8-4a2c-4bce-8d8f-00fc664a4e5b',
                isInUse: false,
                isPinnable: false,
                lockedIndex: 13,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src:
                        'https://shell.cdn.office.net/shellux/bookings_mono.3a4233706c113a988a2b7c2397b6bfce.svg',
                },
                secondaryCommands: isMOSLaunchPagesEnabled() ? [AppTilePreviewCommand] : [],
            },
        },
        '0d5c91ee-5be2-4b79-81ed-23e6c4580427': {
            node: {
                __typename: 'M365HubApplication',
                accessibleName: navBarLinkData.Text,
                badge: {
                    __typename: 'M365ApplicationNumericBadge',
                    count: 0,
                    countDescription: 'Empty',
                },
                brandingColor: theme.themePrimary,
                canLaunchOut: true,
                command: {
                    command: {
                        stateTransition: 'flush',
                        visibilityState: 'show',
                    },
                    entity: {
                        action: 'default',
                        entityId: '0d5c91ee-5be2-4b79-81ed-23e6c4580427',
                        type: 'outlook_todo',
                    },
                },
                icon: {
                    __typename: 'RemoteImage',
                    src: `${CDN_URL}todo/publicIcons/todo-metaos-32.png`,
                },
                id: '0g12a265-8af0-40c6-a8a7-99c1402da0e4',
                isInUse: false,
                isPinnable: false,
                lockedIndex: 5,
                name: navBarLinkData.Text || '',
                pinnedIndex: null,
                selectedStateIconOverride: {
                    __typename: 'RemoteImage',
                    src: `${CDN_URL}todo/publicIcons/todo-metaos-32.png`,
                },
                secondaryCommands: isMOSLaunchPagesEnabled() ? [AppTilePreviewCommand] : [],
            },
        },
    };
    return apps[appId];
}
